import { FC, useState } from "react";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Button, Chip, ListSubheader, Menu, MenuItem, ToggleButton, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "./Tabs.module.scss";
import Box from "@mui/material/Box";
import { saveSettings } from "@/controllers/auth";
import { authenticate } from "@/controllers/auth";
import { useAtom } from "jotai";
import { userState } from "@/lib/store";
import SaveIcon from "@mui/icons-material/Save";
import SnackBar from "../snack-bar/SnackBar";

export interface TabItem {
    title: string;
    value: string;
    badge?: string;
    /**
     * A family of tabs shown as one dropdown button. The button is selected
     * when any child is the active tab and then names that child; the value
     * is never navigated to itself. Eleven vehicle registers as eleven
     * buttons was the problem the overflow dropdown solved once already.
     */
    children?: TabItem[];
    /**
     * The heading this tab is listed under once it is in the overflow dropdown.
     *
     * Seventy datasets in one flat list is a list you read rather than scan;
     * under "Financials", "Healthcare" and "Autos" a reader looking for banks
     * reads one heading. Consecutive tabs with the same section share a heading.
     * Ignored for tabs shown as buttons.
     */
    section?: string;
}

interface PropsTypes {
    tabs: TabItem[];
    activeTab: string;
    module: string;
    route: string;
    setState?: (state: string) => void;
    handleChange: (event: React.MouseEvent<HTMLElement>, newAlignment: string) => void;
    alignment?: "flex-start" | "flex-end" | "center";
    setDefault: boolean;
    /**
     * Keep at most this many tabs as buttons and move the rest into a dropdown.
     *
     * A ToggleButtonGroup has no overflow behaviour of its own: it lays every tab
     * out in one row and lets the row grow, so a section that gains a tab per
     * dataset eventually renders a strip wider than the window. Beyond about
     * eight the strip also stops being scannable — the reader is reading a list,
     * not choosing from a set — so this is a legibility limit rather than only a
     * width one. Omit it and the component behaves exactly as before.
     */
    maxVisible?: number;
    /** Wording on the dropdown button when the active tab is not inside it. */
    overflowLabel?: string;
}

/**
 * Split a tab list into the ones shown as buttons and the ones in the dropdown.
 *
 * Pure and exported so the rule is testable without rendering. The active tab is
 * deliberately NOT pulled forward into the visible set: doing so reorders the
 * strip under the reader every time they navigate, and the dropdown button
 * already names the active tab when it holds it.
 */
export const splitTabs = (tabs: TabItem[], maxVisible?: number): { visible: TabItem[]; overflow: TabItem[] } => {
    if (!maxVisible || maxVisible >= tabs.length) return { visible: tabs, overflow: [] };
    // One button short of the limit would be silly: a dropdown holding a single
    // entry is more clicks than the button it replaced.
    if (tabs.length - maxVisible < 2) return { visible: tabs, overflow: [] };
    return { visible: tabs.slice(0, maxVisible), overflow: tabs.slice(maxVisible) };
};

export type OverflowEntry = { kind: "heading"; key: string; title: string } | { kind: "tab"; tab: TabItem };

/**
 * The overflow dropdown's rows: tabs, with a heading wherever the section changes.
 *
 * A family that lands in the dropdown (on a phone, where only one button fits) is
 * listed as its heading followed by its children, rather than as one row whose
 * value is the family's own - which is never a page, and navigating to it led
 * nowhere. Pure and exported so the grouping is testable without rendering.
 */
export const overflowEntries = (tabs: TabItem[]): OverflowEntry[] => {
    const entries: OverflowEntry[] = [];
    let current: string | undefined;
    tabs.forEach((tab) => {
        if (tab.children?.length) {
            entries.push({ kind: "heading", key: `family:${tab.value}`, title: tab.title });
            tab.children.forEach((child) => entries.push({ kind: "tab", tab: child }));
            // Whatever follows a family starts its own heading, even if it shares one with what preceded it.
            current = `family:${tab.value}`;
            return;
        }
        if (tab.section && tab.section !== current) entries.push({ kind: "heading", key: `section:${tab.section}:${entries.length}`, title: tab.section });
        current = tab.section;
        entries.push({ kind: "tab", tab });
    });
    return entries;
};

/** Every navigable tab in a list, with families opened up. Pure. */
export const flattenTabs = (tabs: TabItem[]): TabItem[] => tabs.flatMap((tab) => (tab.children?.length ? tab.children : [tab]));

const Tabs: FC<PropsTypes> = ({ tabs, activeTab, module, route, setState, handleChange, alignment = "flex-start", setDefault, maxVisible, overflowLabel = "More" }) => {
    const [user, setUser] = useAtom(userState);
    const [overflowAnchor, setOverflowAnchor] = useState<null | HTMLElement>(null);

    const { visible, overflow } = splitTabs(tabs, maxVisible);
    const activeInOverflow = flattenTabs(overflow).find((tab) => tab.value === activeTab);
    const [groupAnchor, setGroupAnchor] = useState<{ value: string; element: HTMLElement } | null>(null);
    const openGroup = visible.find((tab) => tab.value === groupAnchor?.value);

    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        text: "",
        severity: "success",
    });

    const handleSetDefault = () => {
        if (user.subscription_plan === "ORTEX Visitor") return setSnackBar({ open: true, text: "You need to be a registered user to save your default tab", severity: "error" });
        saveSettings(module, route, { activeTab })
            .then(authenticate)
            .then((data) => {
                setUser((prevUser) => {
                    return {
                        ...prevUser,
                        settings: {
                            ...prevUser.settings,
                            module_settings: {
                                ...prevUser.settings?.module_settings,
                                [module + "->" + route]: {
                                    ...prevUser.settings?.module_settings?.[module + "->" + route],
                                    activeTab,
                                },
                            },
                        },
                    };
                });
            })
            .then(() => {
                setSnackBar({ open: true, text: `This is now your default tab and will open next time you go to this page`, severity: "success" });
            });
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: alignment, width: "100%", overflow: "hidden", marginX: "auto" }} className={styles.container}>
            <Box sx={{ width: "fit-content" }} className={styles.selector}>
                <ToggleButtonGroup size="small" color="primary" exclusive value={activeTab} onChange={handleChange} className={styles["button-group"]}>
                    {visible.map((tab) => {
                        if (tab.children?.length) {
                            const activeChild = tab.children.find((child) => child.value === activeTab);
                            return (
                                <ToggleButton
                                    key={tab.value}
                                    value={tab.value}
                                    selected={!!activeChild}
                                    className={styles.button}
                                    onClick={(event) => {
                                        // Same reason as the overflow button below: defaulting
                                        // the event keeps the group's value out of onChange.
                                        event.preventDefault();
                                        setGroupAnchor({ value: tab.value, element: event.currentTarget as HTMLElement });
                                    }}
                                >
                                    <Typography variant="minorSubtitle"> {activeChild ? `${tab.title}: ${activeChild.title}` : tab.title}</Typography>
                                    <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.25 }} />
                                </ToggleButton>
                            );
                        }
                        return (
                            <ToggleButton key={tab.value} value={tab.value} className={visible.length === 1 ? styles.oneButton : styles.button}>
                                <Typography variant="minorSubtitle"> {tab.title}</Typography>
                                {tab.badge && <Chip label={tab.badge} size="small" color="primary" sx={{ ml: 0.5, height: 14, fontSize: "0.55rem", fontWeight: 700, "& .MuiChip-label": { px: 0.5 } }} />}
                            </ToggleButton>
                        );
                    })}
                    {/* The overflow lives inside the group so it shares the group's
                        border and reads as the last segment of the same control
                        rather than as a second, unrelated button. */}
                    {!!overflow.length && (
                        <ToggleButton
                            value="__overflow__"
                            selected={!!activeInOverflow}
                            className={styles.button}
                            onClick={(event) => {
                                // ToggleButton calls its own onClick before the group's
                                // onChange and skips the latter when the event is
                                // defaulted. Without this the sentinel value would be
                                // handed to the consumer as if it were a tab, and the
                                // section would navigate to `__overflow__`.
                                event.preventDefault();
                                setOverflowAnchor(event.currentTarget as HTMLElement);
                            }}
                        >
                            {/* Naming the active tab here is what stops the dropdown
                                hiding where you are: without it the strip shows no
                                selection at all on every page it holds. */}
                            <Typography variant="minorSubtitle"> {activeInOverflow ? activeInOverflow.title : overflowLabel}</Typography>
                            <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.25 }} />
                        </ToggleButton>
                    )}
                </ToggleButtonGroup>
                <Menu anchorEl={groupAnchor?.element ?? null} open={Boolean(groupAnchor)} onClose={() => setGroupAnchor(null)} sx={{ maxHeight: "70vh" }}>
                    {/* A family's children carry sub-headings the same way the overflow
                        does: Transport lists its airlines under "Airlines" and its
                        airports under "Airports" rather than as one run of names. */}
                    {overflowEntries(openGroup?.children ?? []).map((entry) =>
                        entry.kind === "heading" ? (
                            <ListSubheader key={entry.key} sx={{ lineHeight: "28px", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                                {entry.title}
                            </ListSubheader>
                        ) : (
                            <MenuItem
                                key={entry.tab.value}
                                selected={entry.tab.value === activeTab}
                                onClick={(event) => {
                                    setGroupAnchor(null);
                                    handleChange(event, entry.tab.value);
                                }}
                            >
                                <Typography variant="minorSubtitle">{entry.tab.title}</Typography>
                            </MenuItem>
                        )
                    )}
                </Menu>
                <Menu anchorEl={overflowAnchor} open={Boolean(overflowAnchor)} onClose={() => setOverflowAnchor(null)} sx={{ maxHeight: "70vh" }}>
                    {overflowEntries(overflow).map((entry) =>
                        entry.kind === "heading" ? (
                            // Sticky, so a reader scrolled deep into Autos still sees which heading they are under.
                            <ListSubheader key={entry.key} sx={{ lineHeight: "28px", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                                {entry.title}
                            </ListSubheader>
                        ) : (
                            <MenuItem
                                key={entry.tab.value}
                                selected={entry.tab.value === activeTab}
                                onClick={(event) => {
                                    setOverflowAnchor(null);
                                    handleChange(event, entry.tab.value);
                                }}
                            >
                                <Typography variant="minorSubtitle">{entry.tab.title}</Typography>
                                {entry.tab.badge && <Chip label={entry.tab.badge} size="small" color="primary" sx={{ ml: 0.5, height: 14, fontSize: "0.55rem", fontWeight: 700, "& .MuiChip-label": { px: 0.5 } }} />}
                            </MenuItem>
                        )
                    )}
                </Menu>
            </Box>
            {setDefault && (
                <Tooltip arrow title={"Save tab as default"}>
                    <Button sx={{ width: "fit-content", minWidth: "25px", paddingX: 0 }} onClick={handleSetDefault} disabled={user.subscription_plan === "ORTEX Visitor"}>
                        <SaveIcon fontSize="small" />
                    </Button>
                </Tooltip>
            )}
            <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />
        </Box>
    );
};

export default Tabs;
